import React, { useState } from 'react'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { Input } from '@/components/ui/input.jsx'
import { Label } from '@/components/ui/label.jsx'
import { Textarea } from '@/components/ui/textarea.jsx'
import { Plus, Minus, ShoppingCart, Star, ChevronLeft, ChevronRight } from 'lucide-react'
import { getAssetUrl } from '@/hooks/useAsset'

function OrderPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [selectedPackage, setSelectedPackage] = useState('single')
  const [cart, setCart] = useState([])

  const productImages = [
    { src: getAssetUrl("/img-1.jpg"), alt: "Gourmet Lobster Tail" },
    { src: getAssetUrl("/img-2.jpg"), alt: "Commercial Kitchen Preparation" },
    { src: getAssetUrl("/img-3.jpg"), alt: "Serving Fresh Lobster" },
    { src: getAssetUrl("/img-4.jpg"), alt: "Premium Catering Setup" }
  ]

  const productPackages = [
    { 
      id: 'single', 
      name: 'Single Tail', 
      price: 20, 
      description: 'Perfect for trying our signature lobster tail',
      popular: false 
    },
    { 
      id: 'four', 
      name: '4 Tails', 
      price: 64, 
      pricePerTail: 16, 
      description: 'Great for sharing with friends',
      popular: true 
    },
    { 
      id: 'eight', 
      name: '8 Tails', 
      price: 112, 
      pricePerTail: 14, 
      description: 'Perfect for parties and events',
      popular: false 
    }
  ]

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length)
  }

  const adjustQuantity = (change) => {
    setQuantity(prev => Math.max(1, prev + change))
  }

  const addToCart = () => {
    const selectedProduct = productPackages.find(p => p.id === selectedPackage)
    const cartItem = {
      id: Date.now(),
      package: selectedProduct,
      quantity: quantity,
      total: selectedProduct.price * quantity
    }
    setCart(prev => [...prev, cartItem])
  }

  const getTotalPrice = () => {
    const selectedProduct = productPackages.find(p => p.id === selectedPackage)
    return selectedProduct.price * quantity
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {/* Product Images */}
          <div className="space-y-6">
            <div className="relative">
              <div className="relative overflow-hidden rounded-[50px] shadow-2xl">
                <img 
                  src={productImages[currentImageIndex].src}
                  alt={productImages[currentImageIndex].alt}
                  className="w-full h-96 md:h-[500px] object-cover transition-all duration-500"
                />
                
                {/* Navigation Arrows */}
                <button 
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                
                <button 
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
              
              {/* Thumbnail Navigation */}
              <div className="flex justify-center mt-4 space-x-2">
                {productImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentImageIndex 
                        ? 'bg-primary scale-125' 
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-8">
            <div>
              <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">
                🦞 Premium Gourmet Experience
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Deep-Fried 
                <span className="text-primary block">Lobster Tails</span>
              </h1>
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="text-muted-foreground">(4.9/5 from 200+ reviews)</span>
              </div>
              <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
                Experience the ultimate in gourmet street food with our signature deep-fried lobster tails. 
                Prepared with Michelin-inspired techniques and our proprietary secret seasoning blend.
              </p>
            </div>

            {/* Package Selection */}
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-foreground">Choose Your Package</h3>
              <div className="space-y-3">
                {productPackages.map((pkg) => (
                  <div key={pkg.id} className="relative">
                    <input
                      type="radio"
                      id={pkg.id}
                      name="package"
                      value={pkg.id}
                      checked={selectedPackage === pkg.id}
                      onChange={(e) => setSelectedPackage(e.target.value)}
                      className="sr-only"
                    />
                    <label
                      htmlFor={pkg.id}
                      className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedPackage === pkg.id
                          ? 'border-primary bg-primary/10'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold text-lg">{pkg.name}</h4>
                              {pkg.popular && (
                                <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                              )}
                            </div>
                            <p className="text-muted-foreground text-sm">{pkg.description}</p>
                            {pkg.pricePerTail && (
                              <p className="text-primary text-sm font-medium">${pkg.pricePerTail} per tail</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">${pkg.price}</div>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Quantity Selection */}
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-foreground">Quantity</h3>
              <div className="flex items-center space-x-4">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => adjustQuantity(-1)}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-2xl font-semibold w-16 text-center">{quantity}</span>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => adjustQuantity(1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Product Features */}
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-foreground">What Makes Tailtations Special</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-muted-foreground">Cooked at 365°F for 6+ minutes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-muted-foreground">Instagram-friendly packaging</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-muted-foreground">Proprietary secret seasoning</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-muted-foreground">Premium quality sourcing</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                <span className="text-xl font-semibold">Total:</span>
                <span className="text-3xl font-bold text-primary">${getTotalPrice()}</span>
              </div>
              
              <Button 
                size="lg" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg py-6"
                onClick={addToCart}
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Buy Now
              </Button>
            </div>
          </div>
        </div>

        {/* Cart Summary */}
        {cart.length > 0 && (
          <div className="mt-16">
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ShoppingCart className="h-5 w-5" />
                  <span>Cart Summary</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                      <div>
                        <h4 className="font-semibold">{item.package.name}</h4>
                        <p className="text-muted-foreground">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-primary">${item.total}</div>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">Total:</span>
                      <span className="text-3xl font-bold text-primary">
                        ${cart.reduce((sum, item) => sum + item.total, 0)}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="lg" 
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg py-6"
                  >
                    Proceed to Checkout
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrderPage